// src/pages/TenantPortalPages.tsx
import type React from "react";
import { BarChart3, Box, BriefcaseBusiness, CreditCard, FileText, Network } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function CompanyModulePlaceholder({
  title,
  description,
  icon,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <PageHeader title={title} description={description} />
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center gap-4 px-6 py-12 text-center">
          <div className="rounded-3xl bg-muted p-4 text-muted-foreground">{icon}</div>
          <div className="max-w-2xl space-y-2">
            <h2 className="text-xl font-semibold text-foreground">Módulo administrativo da Empresa</h2>
            <p className="text-sm text-muted-foreground">
              Esta área pertence ao contexto administrativo da Empresa e será conectada aos dados reais
              conforme a evolução dos módulos comerciais e financeiros.
            </p>
          </div>
          {children ? <div className="pt-2">{children}</div> : null}
        </CardContent>
      </Card>
    </div>
  );
}

export function TenantCreditsPage() {
  return (
    <CompanyModulePlaceholder
      title="Créditos"
      description="Gestão dos créditos contratados, distribuídos e disponíveis para uso pelos Clientes da Empresa."
      icon={<CreditCard className="h-8 w-8" />}
    />
  );
}

export function TenantConsumptionPage() {
  return (
    <CompanyModulePlaceholder
      title="Consumo"
      description="Acompanhamento do histórico de consumo de vidas, e-mails, armazenamento e tokens de AI."
      icon={<BarChart3 className="h-8 w-8" />}
    />
  );
}

const COMPANY_MODEL_CARDS = [
  {
    title: "Questionários modelo",
    description: "Modelos de questionários da Empresa, como HSE-IT, COPSOQ-BR e modelos próprios.",
    icon: FileText,
    route: "/tenant/modelos/questionarios",
    buttonLabel: "Gerenciar modelos",
  },
  {
    title: "Departamentos modelo",
    description: "Cadastre departamentos padrão da Empresa para importar nos Clientes.",
    icon: Network,
    route: "/tenant/modelos/departamentos",
    buttonLabel: "Gerenciar modelos",
  },
  {
    title: "Cargos modelo",
    description: "Cadastre cargos padrão da Empresa para importar nos Clientes.",
    icon: BriefcaseBusiness,
    route: "/tenant/modelos/cargos",
    buttonLabel: "Gerenciar modelos",
  },
  {
    title: "Estrutura GRO da Empresa",
    description: "Gerencie riscos, dimensões, relacionamentos, Planos de Ação e ações reutilizáveis nos Clientes.",
    icon: Box,
    route: "/tenant/modelos/gro",
    buttonLabel: "Gerenciar modelos",
  },
] as const;

export function TenantModelsPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Modelos da Empresa"
        description="Modelos reutilizáveis da Empresa para acelerar a configuração e a operação dos Clientes."
      />

      <div className="grid auto-rows-fr gap-4 md:grid-cols-2">
        {COMPANY_MODEL_CARDS.map((model) => {
          const Icon = model.icon;

          return (
            <Card key={model.route} className="flex h-full flex-col transition-shadow hover:shadow-md">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  {model.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col items-start gap-4 text-sm text-muted-foreground">
                <p className="flex-1">{model.description}</p>
                <Button variant="outline" onClick={() => navigate(model.route)}>
                  {model.buttonLabel}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
